import { useState } from 'react';
import { isEmpty } from 'lodash';
import { useQuery } from '@tanstack/react-query';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { getHttpService } from '@phoenix/core/frontend/shared/domain/http/http.factory';
import { useCore } from '@phoenix/core/frontend/shared/feature/context/core.context';

type EndUserNotification = {
  id: string;
  content: string;
  labels: string[];
  metaData: string;
  senderId: string;
  status: string;
};

export type Notification = {
  id: string;
  productId: string;
  serialNumber: string;
};

interface UseNotifications {
  notification?: Notification;
  onDismiss: (notificationId?: string) => Promise<unknown>;
}

export const useNotifications = (): UseNotifications => {
  const {
    auth: { user },
  } = useCore();
  const { 'service-reminder-notification': flagEnabled } = useFlags();
  const [dismissed, setDismissed] = useState<string[]>([]);

  const { data } = useQuery(
    ['customer-products-notifications'],
    async () =>
      await getHttpService(process.env.NEXT_PUBLIC_END_USER_NOTIFICATIONS_ENDPOINT).post('/end-user-notification', {
        language: 'en-US',
        recipient: user?.stihl_id,
        recipientType: 'CUSTOMER',
        status: 'NEW_AND_UNREAD',
      }),
    { enabled: flagEnabled }
  );

  const mapQueryResponse = (notifications: EndUserNotification[]) =>
    notifications
      .filter((notification) => !dismissed.includes(notification.id))
      .map((notification) => ({
        id: notification.id,
        productId: JSON.parse(notification.metaData).productId,
        serialNumber: JSON.parse(notification.metaData).serialNumber,
      }))[0];

  const onDismiss = async (notificationId: string) => {
    setDismissed([...dismissed, notificationId]);
    await getHttpService(process.env.NEXT_PUBLIC_END_USER_NOTIFICATIONS_ENDPOINT).put(`/end-user-notification/${notificationId}?read=true`);
  };

  if (isEmpty(data)) {
    return { notification: undefined, onDismiss };
  }

  return { notification: mapQueryResponse(data as EndUserNotification[]), onDismiss };
};
